import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Login.css';
import BASE_URL from '../utils/api';

function Login() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        login: '',
        senha: ''
    });
    const [tiposUsuario, setTiposUsuario] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    // Após muitas senhas erradas o backend exige um código enviado por e-mail
    const [requerCodigo, setRequerCodigo] = useState(false);
    const [codigo, setCodigo] = useState('');
    const [info, setInfo] = useState('');
    // Bloqueio temporário por senhas erradas: contagem regressiva em minutos e segundos
    const [bloqueioFim, setBloqueioFim] = useState(null);
    const [segundosRestantes, setSegundosRestantes] = useState(0);

    useEffect(() => {
        if (!bloqueioFim) return undefined;
        const atualizar = () => {
            const restante = Math.max(0, Math.ceil((bloqueioFim - Date.now()) / 1000));
            setSegundosRestantes(restante);
            if (restante === 0) {
                setBloqueioFim(null);
                setError();
            }
        };
        atualizar();
        const intervalo = setInterval(atualizar, 1000);
        return () => clearInterval(intervalo);
    }, [bloqueioFim]);

    const formatarContagem = (total) =>
        `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;

    // URL do backend// Função para fazer chamadas à API
    const apiCall = async (endpoint, method = 'GET', data = null) => {
        const url = `${BASE_URL}/${endpoint}`;
        console.log(`🌐 ${method} ${url}`);
        
        const config = {
            method,
            headers: {
                'Content-Type': 'application/json',
            },
        };
        
        if (data && method !== 'GET') {
            config.body = JSON.stringify(data);
        }

        const response = await fetch(url, config);
        
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || `Erro ${response.status}`);
        }
        
        return await response.json();
    };

    // Carregar tipos de usuário ao montar o componente
    useEffect(() => {
        fetchTiposUsuario();
        checkLoginStatus();
    }, []);

    const fetchTiposUsuario = async () => {
        try {
            console.log('📊 Carregando tipos de usuário...');
            const tipos = await apiCall('TipoUsuario');
            setTiposUsuario(Array.isArray(tipos) ? tipos : []);
            console.log('✅ Tipos de usuário carregados:', tipos);
        } catch (error) {
            console.error('❌ Erro ao carregar tipos de usuário:', error);
            setTiposUsuario([]);
        }
    };

    // Verificar se já está logado
    const checkLoginStatus = () => {
        const token = localStorage.getItem('authToken');
        const user = localStorage.getItem('userInfo');
        
        if (token && user) {
            setIsLoggedIn(true);
            setUserInfo(JSON.parse(user));
        }
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError(''); // Limpar erro ao digitar
        if (name === 'login') {
            // O código vale só para o login que o recebeu
            setRequerCodigo(false);
            setCodigo('');
            setInfo('');
        }
    };

    // Chamada de login lendo o JSON também nas respostas de erro (mensagem, requerCodigo, bloqueio)
    const postAuth = async (endpoint, data) => {
        const response = await fetch(`${BASE_URL}/${endpoint}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        const body = await response.json().catch(() => ({}));
        return { ok: response.ok, status: response.status, body };
    };

    const handleReenviarCodigo = async () => {
        setError('');
        setInfo('');
        try {
            const { ok, body } = await postAuth('Auth/login/reenviar-codigo', { Login: formData.login });
            if (ok) {
                setInfo(body.mensagem || 'Código reenviado.');
            } else {
                setError(body.mensagem || 'Não foi possível reenviar o código.');
            }
        } catch (error) {
            setError('Não foi possível reenviar o código.');
        }
    };

    // Handle login submission
    const handleLogin = async (e) => {
        e.preventDefault();

        // Block duplicate submissions
        if (loading || bloqueioFim) return;
        
        if (!formData.login || !formData.senha) {
            setError('Por favor, preencha todos os campos.');
            return;
        }

        if (requerCodigo && !codigo.trim()) {
            setError('Informe o código enviado para o seu e-mail.');
            return;
        }

        setLoading(true);
        setError('');
        setInfo('');

        try {
            console.log('🔑 Tentando fazer login...');
            const loginData = {
                Login: formData.login,
                Senha: formData.senha,
                ...(requerCodigo ? { Codigo: codigo.trim() } : {})
            };

            const { ok, body: response } = await postAuth('Auth/login', loginData);

            if (!ok) {
                if (response.requerCodigo) {
                    setRequerCodigo(true);
                    setCodigo('');
                }
                if (response.bloqueadoSegundos > 0) {
                    setBloqueioFim(Date.now() + response.bloqueadoSegundos * 1000);
                    setError('');
                    return;
                }
                setError(response.mensagem || 'Login ou senha inválidos. Tente novamente.');
                return;
            }

            if (response.token) {
                // O login já retorna os dados completos do usuário — sem chamada extra
                const userInfo = {
                    id: response.usuario?.id || response.usuario?.Id,
                    login: response.usuario?.login || response.usuario?.Login || formData.login,
                    tipoUsuarioLoginId: response.usuario?.tipoUsuarioLoginId || response.usuario?.TipoUsuarioLoginId,
                    tipoUsuarioId: response.usuario?.tipoUsuarioId || response.usuario?.TipoUsuarioId,
                    empresaContaId: response.usuario?.empresaContaId || response.usuario?.EmpresaContaId,
                    tenantCNPJ: response.tenant?.cnpj || response.tenant?.CNPJ
                };
                
                // Salvar token e informações do usuário
                localStorage.setItem('authToken', response.token);
                localStorage.setItem('userInfo', JSON.stringify(userInfo));
                localStorage.setItem('usuarioSistemaId', userInfo.id);
                
                // Debug: verificar token salvo
                const savedToken = localStorage.getItem('authToken');
                console.log('[LOGIN] Token salvo no localStorage:', savedToken ? savedToken.substring(0, 40) + '...' : 'NULO');
                console.log('[LOGIN] Token tem 3 partes (JWT válido):', savedToken ? savedToken.split('.').length === 3 : false);
                
                setFormData({ login: '', senha: '' });
                
                console.log('✅ Login bem-sucedido:', userInfo);
                // Redirecionar para Home e recarregar a página
                window.location.href = '/sidebar/Home';
            } else {
                setError('Resposta inválida do servidor.');
            }
            
        } catch (error) {
            console.error('❌ Erro no login:', error);
            setError(error.message || 'Login ou senha inválidos. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    // Handle logout
    const handleLogout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('usuarioSistemaId');
        setIsLoggedIn(false);
        setUserInfo(null);
        setFormData({ login: '', senha: '' });
        alert('Logout realizado com sucesso!');
    };

    const toggleShowPassword = () => setShowPassword(s => !s);

    // Encontrar descrição do tipo de usuário
    const getTipoUsuarioDescricao = (tipoId) => {
        const tipo = tiposUsuario.find(t => (t.id || t.Id) === tipoId);
        return tipo ? (tipo.descricaoTpUsuario || tipo.DescricaoTpUsuario || 'N/A') : 'N/A';
    };

    // Se já está logado, redirecionar direto para Home
    if (isLoggedIn && userInfo) {
        navigate('/sidebar/Home');
        return null;
    }

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)' }}>
            <header style={{
                background: 'linear-gradient(90deg, rgb(235, 133, 0) 0%, rgb(250, 190, 40) 100%)',
                height: 70,
                display: 'flex',
                alignItems: 'center',
                padding: '0 24px',
                justifyContent: 'space-between',
                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                flexShrink: 0
            }}>
                <span style={{ color: '#fff', fontWeight: 800, fontSize: 'clamp(18px, 3vw, 24px)', letterSpacing: '-0.5px' }}>S6 Gestão</span>
                <Link to="/sidebar/CriarConta" style={{ textDecoration: 'none' }}>
                    <button style={{ padding: '10px 30px', fontSize: 15, fontWeight: 700, background: 'transparent', color: '#fff', border: '2px solid rgba(255,255,255,0.9)', borderRadius: 8, cursor: 'pointer' }}>Criar Conta</button>
                </Link>
            </header>

            <div style={{ padding: '12px 24px' }}>
                <Link to="/" style={{ textDecoration: 'none' }}>
                    <button style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'transparent', border: '1px solid #ccc', borderRadius: 8, color: '#555', fontSize: 14, cursor: 'pointer' }}>← Início</button>
                </Link>
            </div>

            <div className="login-container" style={{ flex: 1, minHeight: 'unset', background: 'transparent' }}>
            <div className="login-card">
                <div className="login-header">
                    <h2>🔐 Login do Sistema</h2>
                    <p>Entre com suas credenciais para acessar o sistema</p>
                </div>
                
                <form onSubmit={handleLogin} className="login-form">
                    {bloqueioFim && (
                        <div className="error-message">
                            ⏳ Muitas tentativas incorretas. Tente novamente em{' '}
                            <strong>{formatarContagem(segundosRestantes)}</strong>
                        </div>
                    )}
                    {error && (
                        <div className="error-message">
                            ⚠️ {error}
                        </div>
                    )}
                    
                    <div className="form-group">
                        <label htmlFor="login">
                            👤 Login
                        </label>
                        <input
                            type="text"
                            id="login"
                            name="login"
                            value={formData.login}
                            onChange={handleInputChange}
                            placeholder="Digite seu login"
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="senha">
                            🔒 Senha
                        </label>
                        <div className="input-with-icon">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="senha"
                                name="senha"
                                value={formData.senha}
                                onChange={handleInputChange}
                                placeholder="Digite sua senha"
                                required
                                disabled={loading}
                            />
                            <button type="button" onClick={toggleShowPassword} className="password-toggle" aria-label="Mostrar senha">
                                {showPassword ? (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                        <path d="M3 3l18 18" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M17.94 17.94A10.95 10.95 0 0 1 12 20c-5 0-9.27-3-11-8 1.1-2.9 3.2-5.2 5.7-6.6" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M9.88 9.88a3 3 0 0 0 4.24 4.24" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <circle cx="12" cy="12" r="3" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>

                    {requerCodigo && (
                        <div className="form-group">
                            <label htmlFor="codigo">
                                ✉️ Código de verificação
                            </label>
                            <input
                                type="text"
                                id="codigo"
                                name="codigo"
                                value={codigo}
                                onChange={(e) => { setCodigo(e.target.value.replace(/\D/g, '')); setError(''); }}
                                placeholder="Código de 6 dígitos enviado por e-mail"
                                inputMode="numeric"
                                maxLength={6}
                                autoComplete="one-time-code"
                                disabled={loading}
                            />
                            <button type="button" onClick={handleReenviarCodigo} className="criar-conta-link" disabled={loading}
                                style={{ background: 'none', border: 'none', padding: 0, marginTop: 6, cursor: 'pointer' }}>
                                Reenviar código
                            </button>
                        </div>
                    )}

                    {info && (
                        <div style={{ color: '#2e7d32', fontSize: 14 }}>
                            ✅ {info}
                        </div>
                    )}

                    <div className="criar-conta-text">
                        <Link to="/sidebar/EsqueceuSenha" className="criar-conta-link">Esqueceu a senha?</Link>
                    </div>

                    <div className="criar-conta-text">
                        Ainda não tem uma conta? <Link to="/sidebar/CriarConta" className="criar-conta-link">Criar conta</Link>.
                    </div>

                    <div className="login-actions">
                        <button
                            type="submit"
                            className="login-btn"
                            disabled={loading || !!bloqueioFim}
                        >
                            {loading ? '⏳ Entrando...' : bloqueioFim ? `⏳ Aguarde ${formatarContagem(segundosRestantes)}` : '🔑 Entrar'}
                        </button>
                    </div>
                </form>
            </div>
            </div>
        </div>
    );
}

export default Login;